import { useEffect, useState } from 'react';
import { bookingService } from '../../services/booking';
import { generateInvoice } from '../../utils/pdfGenerator';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { useAuth } from '../../context/AuthContext';

export default function Bookings() {
    const { user } = useAuth();
    const [bookings, setBookings] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedBooking, setSelectedBooking] = useState<any | null>(null);

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const data = await bookingService.getBookings();
            setBookings(data);
        } catch (error) {
            console.error('Error fetching bookings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleProcessRefund = async (bookingId: string) => {
        if (window.confirm('Are you sure you want to process a REFUND via Paystack? This action cannot be undone.')) {
            try {
                // Call Edge Function
                const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-refund`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                    },
                    body: JSON.stringify({
                        bookingId,
                        adminEmail: user?.email
                    })
                });

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(result.error || 'Refund failed');
                }

                alert('Refund processed successfully via Paystack!');
                fetchBookings();
                setSelectedBooking(null);
            } catch (error: any) {
                console.error('Error processing refund:', error);
                alert(`Failed to process refund: ${error.message}`);
            }
        }
    };

    const handleEditClick = () => {
        setEditForm(JSON.parse(JSON.stringify(selectedBooking))); // Deep copy
        setIsEditing(true);
    };

    const handleSaveEdit = async () => {
        if (!user?.email) return;

        try {
            // Update Booking Details
            await bookingService.updateBookingDetails(editForm.id, {
                contact_email: editForm.contact_email,
                contact_phone: editForm.contact_phone
            }, user.email);

            // Update Travelers
            for (const traveler of editForm.booking_travelers) {
                await bookingService.updateTraveler(traveler.id, {
                    first_name: traveler.first_name,
                    last_name: traveler.last_name,
                    passport_number: traveler.passport_number,
                    nationality: traveler.nationality,
                    age: traveler.age,
                    gender: traveler.gender
                }, user.email);
            }

            alert('Booking updated successfully');
            setIsEditing(false);
            fetchBookings();
            setSelectedBooking(null);
        } catch (error) {
            console.error('Error updating booking:', error);
            alert('Failed to update booking');
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount: number, currency: string) => {
        if (currency === 'NGN') return `₦${amount.toLocaleString()}`;
        return `$${amount.toLocaleString()}`;
    };

    if (loading) return <div className="p-8 text-center">Loading bookings...</div>;

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">All Bookings</h1>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {bookings.map((booking) => {
                            let details: any = {};
                            try { details = typeof booking.booking_details === 'string' ? JSON.parse(booking.booking_details) : (booking.booking_details || {}); } catch {}
                            
                            return (
                            <tr key={booking.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatDate(booking.created_at)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{details.contact_email || details.email || 'N/A'}</div>
                                    <div className="text-sm text-gray-500">{details.contact_phone || details.phone || 'N/A'}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                    {booking.service_type || booking.package_type || 'General'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {formatCurrency(Number(booking.amount || booking.total_amount || 0), booking.currency || 'NGN')}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize
                    ${(booking.status || '').includes('paid') || booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                booking.status === 'refund_pending' ? 'bg-orange-100 text-orange-800' :
                                                    booking.status === 'refunded' ? 'bg-gray-100 text-gray-800' :
                                                        'bg-red-100 text-red-800'}`}>
                                        {(booking.status || 'unknown').replace('_', ' ')}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 cursor-pointer hover:text-blue-800">
                                    <button onClick={() => { setSelectedBooking(booking); }}>View Details</button>
                                </td>
                            </tr>
                        )})}
                    </tbody>
                </table>
            </div>

            {/* Details Modal */}
            {selectedBooking && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                        {/* Modal Header */}
                        <div className="bg-slate-900 px-6 py-5 flex justify-between items-center sticky top-0 z-10">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <i className="ri-file-list-3-line text-logo-gold-400"></i>
                                Booking Details
                            </h2>
                            <div className="flex items-center space-x-3">
                                {selectedBooking.status === 'refund_pending' && (
                                    <Button
                                        size="sm"
                                        className="bg-orange-500 hover:bg-orange-600 text-white"
                                        onClick={() => handleProcessRefund(selectedBooking.id)}
                                    >
                                        Process Refund
                                    </Button>
                                )}
                                <button
                                    onClick={() => generateInvoice(selectedBooking)}
                                    className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg font-medium flex items-center transition-colors"
                                >
                                    <i className="ri-download-line mr-2"></i>
                                    Download Invoice
                                </button>
                                <button onClick={() => setSelectedBooking(null)} className="text-slate-400 hover:text-white p-2 rounded-lg transition-colors">
                                    <i className="ri-close-line text-2xl"></i>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-8">
                            {/* Contact Info & Meta */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {(() => {
                                    let details: any = {};
                                    try { details = typeof selectedBooking.booking_details === 'string' ? JSON.parse(selectedBooking.booking_details) : (selectedBooking.booking_details || {}); } catch {}
                                    const email = selectedBooking.contact_email || details.contact_email || details.email || 'N/A';
                                    const phone = selectedBooking.contact_phone || details.contact_phone || details.phone || 'N/A';

                                    return (
                                        <>
                                            <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
                                                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                                    <i className="ri-contacts-line text-logo-gold-600"></i> Contact Information
                                                </h3>
                                                <div className="space-y-3">
                                                    <p className="flex justify-between items-center"><span className="text-slate-500">Email</span> <span className="font-medium text-slate-900">{email}</span></p>
                                                    <p className="flex justify-between items-center"><span className="text-slate-500">Phone</span> <span className="font-medium text-slate-900">{phone}</span></p>
                                                </div>
                                            </div>
                                            <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
                                                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                                    <i className="ri-information-line text-logo-gold-600"></i> Booking Meta
                                                </h3>
                                                <div className="space-y-3">
                                                    <p className="flex justify-between items-center"><span className="text-slate-500">Service</span> <span className="font-medium text-slate-900 capitalize">{selectedBooking.service_type || 'General'}</span></p>
                                                    <p className="flex justify-between items-center"><span className="text-slate-500">Status</span> <span className="font-bold text-slate-900 uppercase">{(selectedBooking.status || 'unknown').replace('_', ' ')}</span></p>
                                                    <p className="flex justify-between items-center"><span className="text-slate-500">Total</span> <span className="font-bold text-green-700">{formatCurrency(Number(selectedBooking.amount || 0), selectedBooking.currency || 'NGN')}</span></p>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Travelers */}
                            <div>
                                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <i className="ri-group-line text-logo-gold-600"></i> Travelers
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {(() => {
                                        let details: any = {};
                                        try { details = typeof selectedBooking.booking_details === 'string' ? JSON.parse(selectedBooking.booking_details) : (selectedBooking.booking_details || {}); } catch {}
                                        const travelersList = selectedBooking.booking_travelers || details.passengers || [];
                                        
                                        if (!Array.isArray(travelersList) || travelersList.length === 0) {
                                            return <p className="text-slate-500 italic col-span-2">No traveler details provided.</p>;
                                        }

                                        return travelersList.map((t: any, idx: number) => {
                                            const fname = t.first_name || t.given_name || t.firstName || '';
                                            const lname = t.last_name || t.family_name || t.lastName || '';
                                            const fullName = `${fname} ${lname}`.trim() || `Traveler ${idx + 1}`;
                                            
                                            return (
                                                <div key={t.id || idx} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-bold">
                                                            {fname.charAt(0) || <i className="ri-user-line"></i>}
                                                        </div>
                                                        <p className="font-bold text-slate-800 text-lg">{fullName}</p>
                                                    </div>
                                                    
                                                    <div className="grid grid-cols-2 gap-y-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                                                        <p className="flex flex-col"><span className="text-xs text-slate-400">Passport</span> <span className="font-medium">{t.passport_number || t.passportNumber || 'N/A'}</span></p>
                                                        <p className="flex flex-col"><span className="text-xs text-slate-400">Nationality</span> <span className="font-medium">{t.nationality || 'N/A'}</span></p>
                                                        <p className="flex flex-col"><span className="text-xs text-slate-400">DOB/Age</span> <span className="font-medium">{t.dob || t.born_on || t.age || 'N/A'}</span></p>
                                                        <p className="flex flex-col"><span className="text-xs text-slate-400">Gender</span> <span className="font-medium capitalize">{t.gender || 'N/A'}</span></p>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>

                            {/* Items (Read Only) */}
                            <div className="pb-6">
                                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <i className="ri-map-pin-line text-logo-gold-600"></i> Itinerary Details
                                </h3>
                                <div className="bg-slate-50 border border-slate-100 rounded-xl p-5 shadow-sm space-y-4">
                                    {(() => {
                                        let details: any = {};
                                        try { details = typeof selectedBooking.booking_details === 'string' ? JSON.parse(selectedBooking.booking_details) : (selectedBooking.booking_details || {}); } catch {}
                                        
                                        const items = Array.isArray(selectedBooking.booking_items) && selectedBooking.booking_items.length > 0
                                            ? selectedBooking.booking_items 
                                            : null;

                                        if (items) {
                                            return items.map((item: any, idx: number) => (
                                                <div key={item.id || idx} className="flex justify-between items-start border-b border-slate-200 last:border-0 pb-4 last:pb-0">
                                                    <div>
                                                        <div className="font-bold text-slate-800 capitalize flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full bg-logo-gold-500"></div>
                                                            {item.service_type}
                                                        </div>
                                                        <pre className="text-sm text-slate-600 mt-2 whitespace-pre-wrap font-sans bg-white p-3 rounded-lg border border-slate-100">
                                                            {JSON.stringify(item.details, null, 2)}
                                                        </pre>
                                                    </div>
                                                    <p className="font-bold text-slate-900 bg-white px-3 py-1 rounded-lg border border-slate-100">{formatCurrency(item.price, selectedBooking.currency)}</p>
                                                </div>
                                            ));
                                        } else if (Object.keys(details).length > 0) {
                                            // Fallback for general bookings
                                            return (
                                                <div>
                                                    <div className="font-bold text-slate-800 capitalize flex items-center gap-2 mb-2">
                                                        <div className="w-2 h-2 rounded-full bg-logo-gold-500"></div>
                                                        {selectedBooking.service_type || 'Booking Payload'}
                                                    </div>
                                                    <pre className="text-sm text-slate-600 mt-2 whitespace-pre-wrap font-sans bg-white p-4 rounded-lg border border-slate-100 overflow-x-auto max-h-[300px] overflow-y-auto">
                                                        {JSON.stringify(details, null, 2)}
                                                    </pre>
                                                </div>
                                            );
                                        } else {
                                            return <p className="text-sm text-slate-500 italic">No itinerary items found.</p>;
                                        }
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
